import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'

import { fetchPublicApiDataIfNeeded, callPublicApi } from '../../../actions/api'
import { apiResourceEndpoint } from '../../../constants/routes'
import { upsertForm } from '../../../reducers/form'
import { formStates } from '../../../constants/helper-states'

import InputElement from '../../utils/input'
import * as MASKS from '../../utils/input-masks'
import SHSForm from '../../utils/shs-form'
import { sortOptions } from '../../utils/option-sort'
import { toWholeNumber } from '../../utils/formatters'
import { displayDateKey } from '../../utils/date-keys'
import FormSection from './form-section'
import InquiryBackLink from './back-link'
import { OptionButtons } from './option-buttons'

import setHours from 'date-fns/setHours'
import setMinutes from 'date-fns/setMinutes'
import { DATE_FLEXIBILITY_OPTIONS, NEEDS_VENUE_OPTIONS, NEEDS_BARTENDER_OPTIONS, PREFERRED_CONTACT_OPTIONS, YES_NO_OPTIONS } from '../../../constants/inquiry-options'

const SECTIONS = ['event', 'menu', 'dietary', 'about']
const ANIMATION_DURATION = 320

const INITIAL_FIELDS = {
    formType: 'private_dinner',
    eventName: '',
    eventDate: '',
    eventTime: '',
    headcount: '',
    apiOccasionId: '',
    city: '',
    state: '',
    street: '',
    postal: '',
    dateFlexibility: '',
    needsVenue: '',
    apiCuisineTypeId: '',
    apiServiceStyleId: '',
    apiVibeId: '',
    vibeOther: '',
    restaurantComparison: '',
    beverages: '',
    needsBartender: '',
    hasAllergies: '',
    guestDescription: '',
    message: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    preferredContact: '',
    needsCleanup: '',
    needsRentals: '',
    isFirstTime: '',
    hasGiftCard: '',
    referralSource: '',
    mailingList: false
}

const mapStateToProps = (state) => ({
    apiCuisines: state.apiCuisines,
    apiServiceStyles: state.apiServiceStyles,
    apiVibes: state.apiVibes,
    apiOccasions: state.apiOccasions
})

const PrivateDinnerInquiry = (props) => {
    const { dispatch, apiCuisines, apiServiceStyles, apiVibes, apiOccasions } = props

    const [activeSection, setActiveSection] = useState(0)
    const [completedSections, setCompletedSections] = useState([])
    const [completingSection, setCompletingSection] = useState(null)
    const [submitted, setSubmitted] = useState(false)
    const [errors, setErrors] = useState({})
    const [fields, setFields] = useState(INITIAL_FIELDS)

    useEffect(() => {
        dispatch(fetchPublicApiDataIfNeeded(apiResourceEndpoint('public/api-cuisines', 'LIST')))
        dispatch(fetchPublicApiDataIfNeeded(apiResourceEndpoint('public/api-service-styles', 'LIST')))
        dispatch(fetchPublicApiDataIfNeeded(apiResourceEndpoint('public/api-vibes', 'LIST')))
        dispatch(fetchPublicApiDataIfNeeded(apiResourceEndpoint('public/api-occasions', 'LIST')))
    }, [dispatch])

    const handleChange = (field, value) => {
        setFields(prev => ({ ...prev, [field]: value }))
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }))
        }
    }

    const sectionStatus = (idx) => {
        if (idx === completingSection) {
            return 'completing'
        }
        if (completedSections.includes(idx)) {
            return 'complete'
        }
        if (activeSection === idx) {
            return 'active'
        }
        return 'locked'
    }

    const editSection = (idx) => {
        setActiveSection(idx)
        setCompletedSections(prev => prev.filter(s => s !== idx))
    }

    const advanceSection = (idx) => {
        setErrors({})
        setCompletingSection(idx)
        setTimeout(() => {
            setCompletingSection(null)
            const next = idx + 1
            setCompletedSections(prev => {
                const updated = [...prev, idx]
                return next < SECTIONS.length ? updated.filter(s => s !== next) : updated
            })
            if (next < SECTIONS.length) {
                setActiveSection(next)
                setTimeout(() => {
                    const el = document.getElementById(`section-${next}`)
                    if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                }, 80)
            }
        }, ANIMATION_DURATION)
    }

    const handleSection0 = (formData, sectionFormId) => {
        dispatch(upsertForm({ id: sectionFormId, status: formStates.EMPTY }))
        const customErrors = {}
        if (!fields.dateFlexibility) {
            customErrors.dateFlexibility = 'Required'
        }
        if (!fields.needsVenue) {
            customErrors.needsVenue = 'Required'
        }
        if (Object.keys(customErrors).length > 0) {
            setErrors(customErrors)
            return
        }
        advanceSection(0)
    }

    const handleSection1 = (formData, sectionFormId) => {
        dispatch(upsertForm({ id: sectionFormId, status: formStates.EMPTY }))
        const customErrors = {}
        if (!fields.needsBartender) {
            customErrors.needsBartender = 'Required'
        }
        if (Object.keys(customErrors).length > 0) {
            setErrors(customErrors)
            return
        }
        advanceSection(1)
    }

    const handleSection2 = (formData, sectionFormId) => {
        dispatch(upsertForm({ id: sectionFormId, status: formStates.EMPTY }))
        const customErrors = {}
        if (fields.hasAllergies !== true && fields.hasAllergies !== false) {
            customErrors.hasAllergies = 'Required'
        }
        if (Object.keys(customErrors).length > 0) {
            setErrors(customErrors)
            return
        }
        advanceSection(2)
    }

    const handleSection3 = (formData, sectionFormId) => {
        const payload = { ...fields, headcount: toWholeNumber(fields.headcount) }
        dispatch(callPublicApi(apiResourceEndpoint('public/event-forms', 'CREATE'), payload, sectionFormId))
    }

    const handleSuccess = () => {
        setSubmitted(true)
        window.scrollTo(0, 0)
    }

    const cuisineOptions = sortOptions(apiCuisines).map(c => ({ value: c.id, label: c.name }))
    // 'unsure' is offered to guests only — it is deliberately not a service_styles
    // row, since chef profiles select from that same table.
    const serviceOptions = [
        ...sortOptions(apiServiceStyles).map(s => ({ value: s.id, label: s.name })),
        { value: 'unsure', label: 'Unsure' }
    ]
    const vibeOptions = sortOptions(apiVibes).map(v => ({ value: v.id, label: v.name }))
    const occasionOptions = sortOptions(apiOccasions).map(o => ({ value: o.id, label: o.name }))

    const today = new Date()
    const timeMin = setHours(setMinutes(new Date(), 0), 8)
    const timeMax = setHours(setMinutes(new Date(), 0), 23)

    const section0Summary = [
        displayDateKey(fields.eventDate),
        toWholeNumber(fields.headcount) ? `${toWholeNumber(fields.headcount).toLocaleString()} guests` : null,
        [fields.city, fields.state].filter(Boolean).join(', ')
    ].filter(Boolean).join(' · ')

    const selectedCuisine = cuisineOptions.find(c => c.value === fields.apiCuisineTypeId)
    const selectedService = serviceOptions.find(s => s.value === fields.apiServiceStyleId)
    const selectedVibe = vibeOptions.find(v => v.value === fields.apiVibeId)

    const section1Summary = [
        selectedCuisine ? selectedCuisine.label : null,
        selectedService ? selectedService.label : null,
        fields.apiVibeId === 'other' ? fields.vibeOther : (selectedVibe ? selectedVibe.label : null)
    ].filter(Boolean).join(' · ')

    const section2Summary = fields.hasAllergies === true
        ? 'Allergies in the group'
        : (fields.hasAllergies === false ? 'No allergies' : '')

    const section3Summary = [
        fields.preferredContact ? `Preferred contact: ${fields.preferredContact}` : null,
        fields.referralSource
    ].filter(Boolean).join(' · ')

    const vibeSelectOptions = [...vibeOptions, { value: 'other', label: 'Other (describe below)' }]

    if (submitted) {
        return (
            <>
                <div className="full-page-bg-img" style={{ backgroundImage: 'url("/img/bg/place-setting.jpg")' }} />
                <div className="form-holder">
                    <div className="event-form">
                        <div className="form-header">
                            <img src="/img/icons/tivity-logo.png" alt="Tivity" width="120px" />
                            <h1>{`You're all set!`}</h1>
                            <p>Thank you for your inquiry. Someone from the Tivity team will reach out within 24 hours with chef, menu, and pricing options.</p>
                        </div>
                    </div>
                </div>
            </>
        )
    }

    return (
        <>
            <div className="full-page-bg-img" style={{ backgroundImage: 'url("/img/bg/place-setting.jpg")' }} />
            <div className="form-holder inquiry-form-holder">
                <div className="event-form inquiry-form">
                    <InquiryBackLink />
                    <div className="form-header">
                        <img src="/img/icons/tivity-logo.png" alt="Tivity" width="120px" />
                        <h1>Private Chef Dinner</h1>
                        <p>We want to hear about your event.<br />We will match you with the best Chef within 24 Hours.</p>
                    </div>

                    <div id="section-0">
                        <FormSection
                            number={1}
                            title="Your Event"
                            status={sectionStatus(0)}
                            summary={section0Summary}
                            onEdit={() => editSection(0)}>
                            <SHSForm
                                submitFX={handleSection0}
                                successFX={() => {}}
                                buttonLabel="Continue to Menu &rarr;"
                                formClass="inquiry-section__continue flex-100">
                                <InputElement
                                    type="text"
                                    id="firstName"
                                    label="First Name"
                                    default={fields.firstName}
                                    onChange={handleChange}
                                    placeholder="First Name"
                                    extraClass="flex-50"
                                    required />
                                <InputElement
                                    type="text"
                                    id="lastName"
                                    label="Last Name"
                                    default={fields.lastName}
                                    onChange={handleChange}
                                    placeholder="Last Name"
                                    extraClass="flex-50"
                                    required />
                                <InputElement
                                    type="tel"
                                    id="phone"
                                    label="Cell Phone"
                                    default={fields.phone}
                                    onChange={handleChange}
                                    isFormatted={true}
                                    formatMask={MASKS.phoneMask}
                                    placeholder="(555) 000-0000"
                                    extraClass="flex-50"
                                    required />
                                <InputElement
                                    type="email"
                                    id="email"
                                    label="Email"
                                    default={fields.email}
                                    onChange={handleChange}
                                    placeholder="your@email.com"
                                    extraClass="flex-50"
                                    required />
                                <InputElement
                                    type="text"
                                    id="eventName"
                                    label="Give your event a title!"
                                    default={fields.eventName}
                                    onChange={handleChange}
                                    placeholder="e.g. Jesse's Birthday Dinner"
                                    extraClass="flex-100" />
                                <InputElement
                                    type="datePicker"
                                    dateType="date"
                                    id="eventDate"
                                    label="Event Date"
                                    default={fields.eventDate}
                                    onChange={handleChange}
                                    options={{
                                        minDate: today,
                                        maxDate: new Date(today.getFullYear() + 1, today.getMonth(), today.getDate()),
                                        placeholderText: 'Select event date',
                                        required: true
                                    }}
                                    extraClass="flex-100"
                                    required />
                                <InputElement
                                    type="datePicker"
                                    dateType="time"
                                    id="eventTime"
                                    label="Service Start Time"
                                    default={fields.eventTime}
                                    onChange={handleChange}
                                    options={{
                                        minTime: timeMin,
                                        maxTime: timeMax,
                                        showTimeSelect: true,
                                        showTimeSelectOnly: true,
                                        timeIntervals: 15,
                                        timeCaption: 'Time',
                                        placeholderText: 'Select service time',
                                        dateFormat: 'h:mm aa',
                                        showPopperArrow: false
                                    }}
                                    extraClass="flex-50"
                                    required />
                                <InputElement
                                    type="text"
                                    id="headcount"
                                    label="Number of Guests"
                                    default={fields.headcount}
                                    onChange={handleChange}
                                    placeholder="e.g. 12"
                                    isFormatted={true}
                                    formatMask={MASKS.countMask}
                                    extraClass="flex-50"
                                    required />
                                <InputElement
                                    type="valueSelect"
                                    id="apiOccasionId"
                                    label="Why are you hosting this event?"
                                    default={fields.apiOccasionId}
                                    onChange={handleChange}
                                    initialValueLabel="What is the occasion?"
                                    options={occasionOptions}
                                    extraClass="flex-100" />
                                <InputElement
                                    type="text"
                                    id="city"
                                    label="City"
                                    default={fields.city}
                                    onChange={handleChange}
                                    placeholder="City"
                                    extraClass="flex-50"
                                    required />
                                <InputElement
                                    type="text"
                                    id="state"
                                    label="State"
                                    default={fields.state}
                                    onChange={handleChange}
                                    placeholder="State"
                                    extraClass="flex-50"
                                    required />
                                <InputElement
                                    type="text"
                                    id="postal"
                                    label="Zip Code"
                                    default={fields.postal}
                                    onChange={handleChange}
                                    placeholder="Zip code"
                                    extraClass="flex-50"
                                    required />
                                <InputElement
                                    type="mapboxAddress"
                                    id="street"
                                    label="Street Address"
                                    default={fields.street}
                                    onChange={handleChange}
                                    placeholder="Street address"
                                    autoComplete="street-address"
                                    extraClass="flex-50" />
                                <InputElement
                                    type="textarea"
                                    id="guestDescription"
                                    label="Who is coming to the party?"
                                    default={fields.guestDescription}
                                    onChange={handleChange}
                                    placeholder="e.g. Mix of family and close friends, mostly foodies, corporate group..."
                                    extraClass="flex-100" />
                                <OptionButtons
                                    id="dateFlexibility"
                                    label="Is the date set?"
                                    error={errors.dateFlexibility}
                                    options={DATE_FLEXIBILITY_OPTIONS}
                                    value={fields.dateFlexibility}
                                    onChange={handleChange}
                                    required />
                                <OptionButtons
                                    id="needsVenue"
                                    label="Do you need a venue?"
                                    error={errors.needsVenue}
                                    options={NEEDS_VENUE_OPTIONS}
                                    value={fields.needsVenue}
                                    onChange={handleChange}
                                    required />
                            </SHSForm>
                        </FormSection>
                    </div>

                    <div id="section-1">
                        <FormSection
                            number={2}
                            title="The Menu"
                            status={sectionStatus(1)}
                            summary={section1Summary}
                            onEdit={() => editSection(1)}>
                            <SHSForm
                                submitFX={handleSection1}
                                successFX={() => {}}
                                buttonLabel="Continue to Dietary &rarr;"
                                formClass="inquiry-section__continue flex-100">
                                <InputElement
                                    type="valueSelect"
                                    id="apiCuisineTypeId"
                                    label="Type of Food"
                                    default={fields.apiCuisineTypeId}
                                    onChange={handleChange}
                                    initialValueLabel="Select cuisine type"
                                    options={cuisineOptions}
                                    extraClass="flex-100"
                                    required />
                                <InputElement
                                    type="valueSelect"
                                    id="apiServiceStyleId"
                                    label="Service Style"
                                    default={fields.apiServiceStyleId}
                                    onChange={handleChange}
                                    initialValueLabel="Select service style"
                                    options={serviceOptions}
                                    extraClass="flex-100"
                                    required />
                                <InputElement
                                    type="valueSelect"
                                    id="apiVibeId"
                                    label="Vibe"
                                    default={fields.apiVibeId}
                                    onChange={handleChange}
                                    initialValueLabel="Select a vibe"
                                    options={vibeSelectOptions}
                                    extraClass="flex-100"
                                    required />
                                {fields.apiVibeId === 'other' && (
                                    <InputElement
                                        type="text"
                                        id="vibeOther"
                                        label="Describe the vibe"
                                        default={fields.vibeOther}
                                        onChange={handleChange}
                                        placeholder="e.g. Romantic and intimate, soft candlelight"
                                        extraClass="flex-100" />
                                )}
                                <InputElement
                                    type="textarea"
                                    id="restaurantComparison"
                                    label="If you were going to a restaurant for this event, where would you go?"
                                    default={fields.restaurantComparison}
                                    onChange={handleChange}
                                    placeholder="e.g. Nobu, a local farm-to-table spot, a classic steakhouse..."
                                    extraClass="flex-100"
                                    required />
                                <InputElement
                                    type="text"
                                    id="beverages"
                                    label="What beverages will you be serving?"
                                    default={fields.beverages}
                                    onChange={handleChange}
                                    placeholder="e.g. Wine pairings, cocktails, non-alcoholic"
                                    extraClass="flex-100" />
                                <InputElement
                                    type="textarea"
                                    id="message"
                                    label="Any additional special requests?"
                                    default={fields.message}
                                    onChange={handleChange}
                                    placeholder="Anything else we should know..."
                                    extraClass="flex-100" />
                                <OptionButtons
                                    id="needsBartender"
                                    label="Do you need a bartender?"
                                    error={errors.needsBartender}
                                    options={NEEDS_BARTENDER_OPTIONS}
                                    value={fields.needsBartender}
                                    onChange={handleChange}
                                    required />
                            </SHSForm>
                        </FormSection>
                    </div>

                    <div id="section-2">
                        <FormSection
                            number={3}
                            title="Dietary & Details"
                            status={sectionStatus(2)}
                            summary={section2Summary}
                            onEdit={() => editSection(2)}>
                            <SHSForm
                                submitFX={handleSection2}
                                successFX={() => {}}
                                buttonLabel="Continue to Event Items &rarr;"
                                formClass="inquiry-section__continue flex-100">
                                <OptionButtons
                                    id="hasAllergies"
                                    label="Do you or anyone in your group have an allergy?"
                                    error={errors.hasAllergies}
                                    options={YES_NO_OPTIONS}
                                    value={fields.hasAllergies}
                                    onChange={handleChange}
                                    required />
                            </SHSForm>
                        </FormSection>
                    </div>

                    <div id="section-3">
                        <FormSection
                            number={4}
                            title="Additional Event Items"
                            status={sectionStatus(3)}
                            summary={section3Summary}
                            onEdit={() => editSection(3)}>
                            <SHSForm
                                submitFX={handleSection3}
                                successFX={handleSuccess}
                                buttonLabel="Submit Inquiry"
                                formClass="inquiry-section__continue flex-100">
                                <OptionButtons
                                    id="preferredContact"
                                    label="Preferred contact method"
                                    options={PREFERRED_CONTACT_OPTIONS}
                                    value={fields.preferredContact}
                                    onChange={handleChange} />
                                <OptionButtons
                                    id="needsCleanup"
                                    label="Do you want the chef to manage cleanup?"
                                    options={YES_NO_OPTIONS}
                                    value={fields.needsCleanup}
                                    onChange={handleChange} />
                                <OptionButtons
                                    id="needsRentals"
                                    label="Do you need glassware, plateware, or flatware?"
                                    options={YES_NO_OPTIONS}
                                    value={fields.needsRentals}
                                    onChange={handleChange} />
                                <OptionButtons
                                    id="isFirstTime"
                                    label="Is this your first time with Tivity?"
                                    options={YES_NO_OPTIONS}
                                    value={fields.isFirstTime}
                                    onChange={handleChange} />
                                <OptionButtons
                                    id="hasGiftCard"
                                    label="Do you have a gift card to redeem?"
                                    options={YES_NO_OPTIONS}
                                    value={fields.hasGiftCard}
                                    onChange={handleChange} />
                                <InputElement
                                    type="text"
                                    id="referralSource"
                                    label="How did you hear about Tivity?"
                                    default={fields.referralSource}
                                    onChange={handleChange}
                                    placeholder="e.g. Instagram, a friend, Google..."
                                    extraClass="flex-100" />
                            </SHSForm>
                        </FormSection>
                    </div>
                </div>
            </div>
        </>
    )
}

export default connect(mapStateToProps)(PrivateDinnerInquiry)
